import {
  Autocomplete,
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { InputType } from "src/api/types";

interface Props<T extends object> {
  current: InputType<T>;
}

function Input<T extends object>({ current }: Props<T>) {
  if (current.type === "custom") {
    return <current.component />;
  }

  if (current.type === "text") {
    return (
      <FormControl>
        <TextField
          multiline={current.multiline}
          minRows={current.multiline ? current.minRows : undefined}
          label={current.label}
          required={current.required}
          value={current.value}
          disabled={current.disabled}
          onChange={(e) => current.onChange(e.target.value)}
        />
      </FormControl>
    );
  }

  if (current.type === "number") {
    return (
      <FormControl>
        <TextField
          type={current.type}
          label={current.label}
          required={current.required}
          value={current.value}
          onChange={(e) => current.onChange(Number(e.target.value))}
        />
      </FormControl>
    );
  }

  if (current.type === "select") {
    return (
      <FormControl>
        <TextField
          label={current.label}
          select
          value={current.value}
          required={current.required}
          onChange={(e) => current.onChange(e.target.value)}
        >
          {current.options.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
      </FormControl>
    );
  }

  if (current.type === "checkbox") {
    return (
      <FormControlLabel
        sx={{
          alignItems: "center",
          gap: 1,
          margin: 0,
          // color: "#6C7278",
          "& .MuiFormControlLabel-label": {
            fontSize: "16px",
          },
        }}
        control={
          <Checkbox
            required={current.required}
            checked={current.value}
            onChange={(e) => current.onChange(e.target.checked)}
            sx={{
              padding: 0,
              "&.Mui-checked": {
                color: "#FF8175",
              },
            }}
          />
        }
        label={current.label}
      />
    );
  }

  if (current.type === "autocomplete") {
    return (
      <Autocomplete
        value={current.value}
        onChange={(_, newValue) => current.onChange(newValue ?? null)}
        disablePortal
        options={current.options}
        getOptionLabel={current.getOptionLabel}
        renderInput={(params) => (
          <TextField
            {...params}
            label={current.placeholder}
            required={current.required}
          />
        )}
        renderOption={(props, option) => {
          const { key, ...optionProps } = props;
          const description = current.getOptionDescription?.(option);

          return (
            <Box key={key} {...optionProps} component="li">
              <Stack>
                <Typography textAlign="left">
                  {current.getOptionLabel(option)}
                </Typography>
                {description ? (
                  <Typography
                    textAlign="left"
                    variant="caption"
                    color="text.secondary"
                  >
                    {description}
                  </Typography>
                ) : null}
              </Stack>
            </Box>
          );
        }}
      />
    );
  }
}

export default Input;
